/**
 * Seed Remove Tag Modal Component
 *
 * Follows the same pattern as Technology and Asset sections for UI consistency.
 * Uses shared RemoveTag component for consistent UX across the platform.
 *
 * Key features:
 * - Supports bulk tag removal operations for multiple seeds
 * - Handles Asset, WebApplication, and ADObject seed types
 * - Fetches current seed data before update to prevent data loss
 * - Graceful handling of partial failures
 * - Accurate success/failure reporting with toast notifications
 */

import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { RemoveTag } from '@/sections/RemoveTag';
import { useMutation } from '@/utils/api';
import { tagRemovedError, tagRemovedSuccess } from '@/utils/tagToastMessages';

interface Props {
  open: boolean;
  onClose: () => void;
  seedKeys: string[];
  onSuccess?: () => void;
}

/**
 * Modal component for removing tags from seeds
 *
 * Uses shared RemoveTag component for consistent UX across the platform.
 * Handles both single seed (from drawer) and bulk operations (from table).
 *
 * Features:
 * - Auto-fetches common tags across selected seeds
 * - Visual tag selection interface
 * - Graceful handling of partial failures
 * - Accurate success/failure reporting
 *
 * Seeds can be different types (Asset, WebApplication, ADObject), so we
 * need to determine the correct endpoint based on the seed's class field.
 *
 * Example usage:
 * ```tsx
 * <SeedRemoveTag
 *   open={modalState.removeTagModal}
 *   onClose={modalHandlers.closeRemoveTagModal}
 *   seedKeys={selectedKeys}
 *   onSuccess={clearSelection}
 * />
 * ```
 *
 * @param props - Component props
 * @returns Modal component
 */
export const SeedRemoveTag = ({
  open,
  onClose,
  seedKeys,
  onSuccess,
}: Props) => {
  const axios = useAxios();

  const { mutateAsync: removeTag, status: removingTag } = useMutation({
    defaultErrorMessage: 'Failed to remove tag',
    mutationFn: async (tagToRemove: string) => {
      const promises = seedKeys.map(async seedKey => {
        // Fetch current seed to get both tags AND status
        const currentSeed = await axios.get(`/my`, {
          params: {
            label: 'seed',
            key: seedKey,
            exact: true,
          },
        });

        const seed = currentSeed.data.seeds?.[0];
        const existingTags = seed?.tags || [];
        const currentStatus = seed?.status || 'A';
        const seedClass = seed?.class || 'asset';

        // Remove the tag
        const updatedTags = existingTags.filter(
          (tag: string) => tag !== tagToRemove
        );

        // Determine endpoint based on seed class
        // Seeds can be: asset, webapplication, or adobject
        let endpoint = '/seed';
        if (seedClass === 'webapplication') {
          endpoint = '/webapplication';
        }

        const payload = {
          key: seedKey,
          status: currentStatus, // Include current status (required by backend)
          tags: updatedTags,
        };

        return axios.put(endpoint, payload);
      });

      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled(promises);

      // Count successes and failures
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      // Invalidate cache if any operations succeeded
      if (succeeded > 0) {
        // Use predicate-based invalidation matching Technology pattern
        await queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'seed';
          },
          refetchType: 'active', // Only refetch visible queries for better performance
        });
      }

      // Log failures for debugging with enhanced error details
      if (failed > 0) {
        const failedResults = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r, i) => ({
            seedKey: seedKeys[i],
            error: r.reason,
            errorResponse: r.reason?.response,
            errorData: r.reason?.response?.data,
          }));
        console.error(
          '[SeedRemoveTag] Failed to remove tag from seeds:',
          failedResults
        );
      }

      // Return result summary for handleSubmit to use
      return { succeeded, failed, tagToRemove };
    },
  });

  const handleSubmit = async (tag: string) => {
    try {
      const result = await removeTag(tag);

      // Show accurate feedback based on results
      if (result.succeeded > 0 && result.failed === 0) {
        const count = result.succeeded > 1 ? result.succeeded : undefined;
        toast.success(tagRemovedSuccess('seed', count));
      } else if (result.succeeded > 0 && result.failed > 0) {
        toast.warning(
          `Tag "${result.tagToRemove}" removed from ${result.succeeded} ${result.succeeded > 1 ? 'seeds' : 'seed'}, ` +
            `but failed for ${result.failed} ${result.failed > 1 ? 'seeds' : 'seed'}`
        );
      } else {
        toast.error(
          `Failed to remove tag "${result.tagToRemove}" from ${result.failed} ${result.failed > 1 ? 'seeds' : 'seed'}`
        );
      }

      onClose();
      onSuccess?.();
    } catch (error: any) {
      console.error('[SeedRemoveTag] Tag removal failed:', error);
      toast.error(
        tagRemovedError('seed', error.response?.data?.message || error.message)
      );
    }
  };

  return (
    <RemoveTag
      open={open}
      onClose={onClose}
      disabled={removingTag === 'pending'}
      onSubmit={handleSubmit}
      assetKeys={seedKeys}
      label="seed"
    />
  );
};
